/**
 * Video Synthesizer (Cinema Studio Engine):
 * Gera comerciais de alta conversão completos (WebM/MP4, 10s - 30s)
 * com narrativa em 4 Atos, Transições cinematográficas, Legendas dinâmicas (TikTok/Reels),
 * Iluminação de estúdio (Rim Light), Efeito Ken Burns com partículas e Áudio TTS sincronizado.
 */

export interface VideoScene {
  act?: number;
  type?: 'hook' | 'presentation' | 'cta' | string;
  duration_s?: number;
  visual_description?: string;
  narration_text?: string;
}

export interface VideoSynthesisOptions {
  assetUrl: string;
  headline: string;
  angleName?: string;
  ctaText?: string;
  avatarUrl?: string;
  avatarName?: string;
  avatarRole?: string;
  format?: 'vertical' | 'square';
  durationSeconds?: number;
  videoScript?: string;
  videoScenes?: VideoScene[];
  narrationAudioUrl?: string;
}

export async function synthesizeCreativeVideoBlob(options: VideoSynthesisOptions): Promise<Blob> {
  const isStories = options.format !== 'square';
  const width = 1080;
  const height = isStories ? 1920 : 1080;
  const duration = options.durationSeconds || 10;
  const fps = 30;
  const totalFrames = duration * fps;

  // Carregar imagem de fundo / cenário
  const bgImage = await loadImageWithFallback(
    options.assetUrl || 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1080'
  );

  // Carregar avatar da marca
  let avatarImage: HTMLImageElement | null = null;
  const avatarFallbackUrl = options.avatarUrl || 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800';
  try {
    avatarImage = await loadImageWithFallback(avatarFallbackUrl);
  } catch {
    avatarImage = null;
  }

  // Criar canvas de renderização cinematográfica
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Não foi possível inicializar o contexto Canvas 2D');

  // Preparar gravação com MediaRecorder
  const stream = canvas.captureStream(fps);

  // Se houver áudio de narração TTS, mixa no stream
  let audioElement: HTMLAudioElement | null = null;
  let audioContext: AudioContext | null = null;
  if (options.narrationAudioUrl) {
    try {
      audioElement = new Audio(options.narrationAudioUrl);
      audioElement.crossOrigin = 'anonymous';
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const sourceNode = audioContext.createMediaElementSource(audioElement);
      const destinationNode = audioContext.createMediaStreamDestination();
      sourceNode.connect(destinationNode);
      sourceNode.connect(audioContext.destination);

      const audioTracks = destinationNode.stream.getAudioTracks();
      if (audioTracks.length > 0) {
        stream.addTrack(audioTracks[0]);
      }
    } catch (aErr) {
      console.warn('[VideoSynthesizer] Audio mixing fallback:', aErr);
    }
  }

  const mimeType = MediaRecorder.isTypeSupported('video/mp4;codecs=avc1')
    ? 'video/mp4;codecs=avc1'
    : MediaRecorder.isTypeSupported('video/mp4')
    ? 'video/mp4'
    : MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
    ? 'video/webm;codecs=vp9'
    : 'video/webm';

  const mediaRecorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: 10000000 // 10 Mbps para nitidez cinematográfica máxima
  });

  const recordedChunks: Blob[] = [];
  mediaRecorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) recordedChunks.push(e.data);
  };

  const recordingPromise = new Promise<Blob>((resolve, reject) => {
    mediaRecorder.onstop = () => {
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch(() => {});
      }
      const finalBlob = new Blob(recordedChunks, { type: mimeType.split(';')[0] });
      resolve(finalBlob);
    };
    mediaRecorder.onerror = (err) => reject(err);
  });

  mediaRecorder.start();
  if (audioElement) {
    audioElement.play().catch(() => {});
  }

  // Partículas para atmosfera de estúdio
  const particles = Array.from({ length: 28 }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    size: 2 + Math.random() * 4,
    speedY: -0.8 - Math.random() * 1.5,
    speedX: (Math.random() - 0.5) * 0.8,
    opacity: 0.2 + Math.random() * 0.6
  }));

  // Renderizar frames da cena contínua
  for (let frame = 0; frame <= totalFrames; frame++) {
    const progress = frame / totalFrames; // 0.0 a 1.0
    const currentTime = progress * duration;

    renderCinematicScene(ctx, {
      width,
      height,
      isStories,
      progress,
      currentTime,
      duration,
      bgImage,
      avatarImage,
      headline: options.headline || 'Oferta Exclusiva',
      angleName: options.angleName || 'DESTAQUE',
      ctaText: options.ctaText || 'SAIBA MAIS',
      avatarName: options.avatarName || 'Especialista da Marca',
      avatarRole: options.avatarRole || 'Porta-voz Oficial',
      videoScript: options.videoScript,
      videoScenes: options.videoScenes,
      particles
    });

    await new Promise((r) => setTimeout(r, 1000 / fps));
  }

  if (audioElement) {
    audioElement.pause();
  }
  mediaRecorder.stop();
  return recordingPromise;
}

function renderCinematicScene(
  ctx: CanvasRenderingContext2D,
  params: {
    width: number;
    height: number;
    isStories: boolean;
    progress: number;
    currentTime: number;
    duration: number;
    bgImage: HTMLImageElement;
    avatarImage: HTMLImageElement | null;
    headline: string;
    angleName: string;
    ctaText: string;
    avatarName: string;
    avatarRole: string;
    videoScript?: string;
    videoScenes?: VideoScene[];
    particles: Array<{ x: number; y: number; size: number; speedY: number; speedX: number; opacity: number }>;
  }
) {
  const {
    width,
    height,
    isStories,
    progress,
    currentTime,
    duration,
    bgImage,
    avatarImage,
    headline,
    angleName,
    ctaText,
    avatarName,
    avatarRole,
    videoScript,
    videoScenes,
    particles
  } = params;

  ctx.clearRect(0, 0, width, height);

  // ==========================================
  // CAMADA 1: CENÁRIO DE FUNDO COM PROFUNDIDADE DE CAMPO DINÂMICA
  // ==========================================
  const bgZoom = 1.08 + 0.08 * Math.sin(progress * Math.PI);
  const bgPanX = Math.sin(progress * Math.PI * 0.8) * 35;
  const bgPanY = Math.cos(progress * Math.PI * 0.5) * 20;

  ctx.save();
  ctx.translate(width / 2, height / 2);
  ctx.scale(bgZoom, bgZoom);
  ctx.translate(-width / 2 + bgPanX, -height / 2 + bgPanY);
  drawImageCover(ctx, bgImage, 0, 0, width, height);
  ctx.restore();

  // Overlay com gradiente dramático de estúdio
  const studioGrad = ctx.createRadialGradient(width / 2, height * 0.45, 120, width / 2, height / 2, width * 0.95);
  studioGrad.addColorStop(0, 'rgba(15, 23, 42, 0.25)');
  studioGrad.addColorStop(0.65, 'rgba(15, 23, 42, 0.82)');
  studioGrad.addColorStop(1, 'rgba(3, 7, 18, 0.96)');
  ctx.fillStyle = studioGrad;
  ctx.fillRect(0, 0, width, height);

  // Partículas de luz / poeira cinematográfica no estúdio
  ctx.save();
  for (const p of particles) {
    p.y += p.speedY;
    p.x += p.speedX;
    if (p.y < 0) p.y = height;
    if (p.x < 0) p.x = width;
    if (p.x > width) p.x = 0;

    ctx.fillStyle = `rgba(168, 85, 247, ${p.opacity * 0.5})`;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // ==========================================
  // CAMADA 2: O AVATAR EM PRIMEIRO PLANO COMO PROTAGONISTA
  // ==========================================
  if (avatarImage) {
    const avatarDolly = 1.0 + 0.08 * progress;
    const breathe = Math.sin(currentTime * 2.8) * 5;
    const swayX = Math.sin(currentTime * 1.6) * 8;

    const avtSize = isStories ? 680 : 540;
    const avtX = (width - avtSize) / 2 + swayX;
    const avtY = isStories ? (height * 0.42 - avtSize / 2 + breathe) : (height * 0.45 - avtSize / 2 + breathe);

    ctx.save();
    ctx.translate(width / 2, avtY + avtSize / 2);
    ctx.scale(avatarDolly, avatarDolly);
    ctx.translate(-width / 2, -(avtY + avtSize / 2));

    // Halo de Iluminação Traseira (Rim Light / Studio Glow)
    const rimGrad = ctx.createRadialGradient(width / 2, avtY + avtSize / 2, avtSize * 0.3, width / 2, avtY + avtSize / 2, avtSize * 0.68);
    rimGrad.addColorStop(0, 'rgba(99, 102, 241, 0.45)');
    rimGrad.addColorStop(0.65, 'rgba(168, 85, 247, 0.25)');
    rimGrad.addColorStop(1, 'rgba(99, 102, 241, 0)');
    ctx.fillStyle = rimGrad;
    ctx.beginPath();
    ctx.arc(width / 2, avtY + avtSize / 2, avtSize * 0.68, 0, Math.PI * 2);
    ctx.fill();

    // Moldura Oval Cinematográfica
    ctx.beginPath();
    ctx.ellipse(width / 2, avtY + avtSize / 2, avtSize * 0.44, avtSize * 0.52, 0, 0, Math.PI * 2);
    ctx.clip();

    drawImageCover(ctx, avatarImage, avtX, avtY, avtSize, avtSize);
    ctx.restore();

    // Borda Neon / Vidro
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(width / 2, avtY + avtSize / 2, avtSize * 0.44 * avatarDolly, avtSize * 0.52 * avatarDolly, 0, 0, Math.PI * 2);
    ctx.lineWidth = 6;
    const borderGrad = ctx.createLinearGradient(0, avtY, width, avtY + avtSize);
    borderGrad.addColorStop(0, '#818cf8');
    borderGrad.addColorStop(0.5, '#c084fc');
    borderGrad.addColorStop(1, '#38bdf8');
    ctx.strokeStyle = borderGrad;
    ctx.shadowColor = 'rgba(99, 102, 241, 0.85)';
    ctx.shadowBlur = 22;
    ctx.stroke();
    ctx.restore();
  }

  // ==========================================
  // CAMADA 3: NARRATIVA EM 4 ATOS PUBLICITÁRIOS (0s a duration)
  // ==========================================
  const t1 = duration * 0.28; // Fim do Ato 1 (ex: 2.8s)
  const t2 = duration * 0.65; // Fim do Ato 2 (ex: 6.5s)
  const t3 = duration * 0.85; // Fim do Ato 3 (ex: 8.5s)

  // --- ATO 1: ENTRADA DO HOOK E ÂNGULO ---
  const tagY = isStories ? 160 : 70;
  const tagAlpha = Math.min(1, currentTime * 2.5);

  ctx.save();
  ctx.globalAlpha = tagAlpha;
  ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
  ctx.strokeStyle = '#818cf8';
  ctx.lineWidth = 2;
  roundRect(ctx, width / 2 - 200, tagY, 400, 50, 25);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#c7d2fe';
  ctx.font = 'bold 20px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`✨ ${angleName.toUpperCase()}`, width / 2, tagY + 25);
  ctx.restore();

  // Headline Principal Animada
  const headY = isStories ? 235 : 135;
  const headProgress = Math.min(1, Math.max(0, (currentTime - 0.2) * 2));
  const headOffsetY = (1 - headProgress) * 25;

  ctx.save();
  ctx.globalAlpha = headProgress;
  ctx.shadowColor = 'rgba(0, 0, 0, 0.95)';
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 4;
  ctx.fillStyle = '#ffffff';
  ctx.font = `900 ${isStories ? '48px' : '38px'} system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  wrapTextCentered(ctx, headline, width / 2, headY + headOffsetY, width - 140, isStories ? 58 : 48);
  ctx.restore();

  // --- ATO 2 & 3: IDENTIFICAÇÃO E CREDIBILIDADE DA PERSONA ---
  if (currentTime >= 1.0) {
    const badgeY = isStories ? (height * 0.72) : (height * 0.70);
    const badgeAlpha = Math.min(1, Math.max(0, (currentTime - 1.0) * 2));
    const badgeScale = 0.95 + 0.05 * badgeAlpha;

    ctx.save();
    ctx.globalAlpha = badgeAlpha;
    ctx.translate(width / 2, badgeY);
    ctx.scale(badgeScale, badgeScale);
    ctx.translate(-width / 2, -badgeY);

    const bW = 560;
    const bH = 86;
    const bX = (width - bW) / 2;

    ctx.fillStyle = 'rgba(15, 23, 42, 0.94)';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)';
    ctx.lineWidth = 2;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
    ctx.shadowBlur = 16;
    roundRect(ctx, bX, badgeY, bW, bH, 43);
    ctx.fill();
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(avatarName, width / 2, badgeY + 34);

    ctx.fillStyle = '#38bdf8';
    ctx.font = '600 17px system-ui, -apple-system, sans-serif';
    ctx.fillText(avatarRole.toUpperCase(), width / 2, badgeY + 62);
    ctx.restore();
  }

  // --- ATO 4: CHAMADA PARA AÇÃO FINAL COM PULSAÇÃO ---
  const ctaStartTime = Math.max(2.5, duration - 3.5);
  if (currentTime >= ctaStartTime) {
    const ctaY = isStories ? (height - 230) : (height - 150);
    const ctaProgress = Math.min(1, Math.max(0, (currentTime - ctaStartTime) * 2.2));
    const ctaPulse = 1.0 + 0.06 * Math.sin((currentTime - ctaStartTime) * 6);

    ctx.save();
    ctx.globalAlpha = ctaProgress;
    ctx.translate(width / 2, ctaY + 45);
    ctx.scale(ctaPulse, ctaPulse);
    ctx.translate(-width / 2, -(ctaY + 45));

    const cW = 500;
    const cH = 90;
    const cX = (width - cW) / 2;

    ctx.shadowColor = 'rgba(99, 102, 241, 0.95)';
    ctx.shadowBlur = 30 + 15 * Math.sin(currentTime * 5);

    const ctaGrad = ctx.createLinearGradient(cX, ctaY, cX + cW, ctaY + cH);
    ctaGrad.addColorStop(0, '#4f46e5');
    ctaGrad.addColorStop(0.5, '#7c3aed');
    ctaGrad.addColorStop(1, '#db2777');
    ctx.fillStyle = ctaGrad;

    roundRect(ctx, cX, ctaY, cW, cH, 45);
    ctx.fill();

    ctx.shadowBlur = 0;
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 30px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`👉 ${ctaText.replace(/_/g, ' ').toUpperCase()}`, width / 2, ctaY + cH / 2);
    ctx.restore();
  }

  // ==========================================
  // CAMADA 4: LEGENDAS DINÂMICAS ANIMADAS ESTILO REELS/TIKTOK
  // ==========================================
  const scriptToUse = videoScript || (videoScenes && videoScenes.length > 0 ? videoScenes.map(s => s.narration_text).filter(Boolean).join(' ') : '');
  if (scriptToUse && scriptToUse.trim()) {
    renderDynamicSubtitles(ctx, {
      width,
      height,
      isStories,
      scriptText: scriptToUse,
      currentTime,
      duration
    });
  }
}

/**
 * Renderiza legendas com palavra ativa destacada em tempo real (Word Highlight)
 */
function renderDynamicSubtitles(
  ctx: CanvasRenderingContext2D,
  params: { width: number; height: number; isStories: boolean; scriptText: string; currentTime: number; duration: number }
) {
  const { width, height, isStories, scriptText, currentTime, duration } = params;
  const words = scriptText.trim().split(/\s+/);
  if (words.length === 0) return;

  const wordsPerSecond = words.length / duration;
  const activeWordIdx = Math.min(words.length - 1, Math.floor(currentTime * wordsPerSecond));

  // Agrupa em blocos de 4 palavras para exibição centralizada
  const chunkSize = 4;
  const currentChunkIdx = Math.floor(activeWordIdx / chunkSize);
  const startIdx = currentChunkIdx * chunkSize;
  const chunkWords = words.slice(startIdx, startIdx + chunkSize);

  const subY = isStories ? (height * 0.81) : (height * 0.80);

  ctx.save();
  ctx.font = '900 32px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Mede tamanho total da linha para desenhar container
  const fullChunkText = chunkWords.join(' ');
  const textWidth = ctx.measureText(fullChunkText).width;
  const padX = 30;
  const padY = 16;

  // Fundo translúcido para leitura perfeita
  ctx.fillStyle = 'rgba(3, 7, 18, 0.88)';
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
  ctx.lineWidth = 1.5;
  roundRect(ctx, width / 2 - textWidth / 2 - padX, subY - 24 - padY / 2, textWidth + padX * 2, 48 + padY, 24);
  ctx.fill();
  ctx.stroke();

  // Renderiza palavra por palavra com realce
  let currentX = width / 2 - textWidth / 2;
  for (let i = 0; i < chunkWords.length; i++) {
    const globalIdx = startIdx + i;
    const word = chunkWords[i] + (i < chunkWords.length - 1 ? ' ' : '');
    const wWidth = ctx.measureText(word).width;

    ctx.textAlign = 'left';
    if (globalIdx === activeWordIdx) {
      ctx.fillStyle = '#fde047'; // Amarelo vibrante na palavra atual
      ctx.shadowColor = 'rgba(253, 224, 71, 0.8)';
      ctx.shadowBlur = 14;
    } else if (globalIdx < activeWordIdx) {
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 0;
    } else {
      ctx.fillStyle = '#94a3b8';
      ctx.shadowBlur = 0;
    }

    ctx.fillText(word, currentX, subY);
    currentX += wWidth;
  }

  ctx.restore();
}

function drawImageCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, x: number, y: number, w: number, h: number) {
  const imgAspect = img.width / img.height;
  const boxAspect = w / h;
  let drawW = w;
  let drawH = h;
  let drawX = x;
  let drawY = y;

  if (imgAspect > boxAspect) {
    drawW = h * imgAspect;
    drawX = x + (w - drawW) / 2;
  } else {
    drawH = w / imgAspect;
    drawY = y + (h - drawH) / 2;
  }
  ctx.drawImage(img, drawX, drawY, drawW, drawH);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapTextCentered(ctx: CanvasRenderingContext2D, text: string, centerX: number, startY: number, maxWidth: number, lineHeight: number) {
  const words = text.split(' ');
  let line = '';
  const lines: string[] = [];

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && n > 0) {
      lines.push(line.trim());
      line = words[n] + ' ';
    } else {
      line = testLine;
    }
  }
  lines.push(line.trim());

  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], centerX, startY + i * lineHeight);
  }
}

function loadImageWithFallback(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
      const fallbackImg = new window.Image();
      fallbackImg.onload = () => resolve(fallbackImg);
      fallbackImg.onerror = (e) => reject(e);
      fallbackImg.src = src;
    };
    img.src = src;
  });
}
